import debounce from 'lodash/debounce'
import type { DebouncedFunc } from 'lodash'

import { BatchRegistry } from './batch-registry'
import { ViewportMonitor } from './viewport-monitor'
import { rankByViewportDistance } from './rank-by-viewport-distance'

export interface LazyBatchLoaderOptions {
  /** CSS rootMargin for IntersectionObserver lookahead (default: '150% 0px 150% 0px') */
  lookaheadMargin?: string
  /** Debounce delay in ms to stabilize scroll before fetching (default: 300) */
  stabilizationMs?: number
}

export type FetchBatch = (
  batchIdx: number,
  signal: AbortSignal
) => Promise<void>

/**
 * Lazily loads batches of workflow review items based on viewport
 * visibility.
 *
 * Fetch policy:
 *   - On init(), only batch 0 — the focused range — is fetched eagerly.
 *   - All other batches stay unfetched until an item in that batch enters
 *     the IntersectionObserver's lookahead zone, at which point a debounced
 *     flush enqueues it (ranked by viewport distance if concurrency is
 *     constrained).
 *   - prefetchBatchForItem(itemIdx) is the escape hatch for jump-to-item /
 *     deep-link flows where the caller knows which item will be visible
 *     before IntersectionObserver fires.
 *
 * Note: `createBatchesMiddleOut` arranges batches around the focused range
 * so batch 1 is the first outward neighbor, batch 2 the second, etc. — but
 * this loader does NOT proactively walk them. Batches beyond 0 only fetch
 * on explicit viewport entry or prefetchBatchForItem().
 *
 * The loader orchestrates three collaborators:
 *   - `BatchRegistry` owns batch configuration and fetch lifecycle state.
 *   - `ViewportMonitor` tracks which items are in viewport.
 *   - `rankByViewportDistance` picks which candidate batch to fetch first
 *     when concurrency is constrained.
 *
 * The caller supplies `fetchBatch` at construction time — the loader invokes
 * it with a batch index and an `AbortSignal` and awaits the returned promise.
 * Failures bump an internal retry counter; after `BatchRegistry.MAX_RETRIES`
 * the batch stops being retried. When `destroy()` or `init()` fires while
 * fetches are still in flight, the signal is aborted and any subsequent
 * settlement is ignored — the caller should honor the signal to avoid
 * mutating state on a torn-down component.
 */
export class LazyBatchLoader {
  private static MAX_CONCURRENT_FETCHES = 3

  private registry = new BatchRegistry()
  private monitor: ViewportMonitor
  private debouncedFlush: DebouncedFunc<() => Promise<void>>
  private abortController: AbortController | null = null

  constructor(
    private fetchBatch: FetchBatch,
    options: LazyBatchLoaderOptions = {}
  ) {
    this.monitor = new ViewportMonitor(() => {
      void this.debouncedFlush()
    }, options.lookaheadMargin)
    this.debouncedFlush = debounce(
      () => this.flushPending(),
      options.stabilizationMs ?? 300
    )
  }

  /**
   * Set the batch definitions. Must be called before `init()` — calling
   * `init()` without batches throws.
   */
  setBatches(batches: number[][]): void {
    this.registry.load(batches)
  }

  /**
   * Initialize the viewport monitor and kick off the initial fetch.
   *
   * Idempotent: calling `init()` again tears down any previous observers
   * and in-flight state before setting up fresh ones. Throws if
   * `setBatches()` was not called first (or was called with an empty array).
   */
  init(root: HTMLElement): void {
    this.destroy()

    if (this.registry.batchCount() === 0) {
      throw new Error(
        'LazyBatchLoader.init(): setBatches() must be called with a non-empty array before init().'
      )
    }

    this.abortController = new AbortController()
    this.monitor.attach(root)
    void this.executeFetch(0)
  }

  /**
   * Tear down the viewport monitor and cancel pending work. Preserves the
   * batch configuration so `init()` can be called again without re-invoking
   * `setBatches()`. Any in-flight fetches see their `AbortSignal` aborted,
   * and their settlement is discarded so they cannot mutate the fresh
   * runtime state of a subsequent `init()`.
   */
  destroy(): void {
    this.abortController?.abort()
    this.abortController = null
    this.debouncedFlush.cancel()
    this.monitor.detach()
    this.registry.resetRuntime()
  }

  isBatchHandled(batchIdx: number): boolean {
    return this.registry.isHandled(batchIdx)
  }

  /**
   * Force-fetch the batch containing the given item, bypassing the
   * viewport-driven debounce. Used by jump-to-item / deep-link flows where
   * the user navigates to an item before scroll has brought its batch into
   * the lookahead zone — without this the item would render a skeleton
   * forever since IntersectionObserver never fires for a batch the user
   * never scrolls past.
   *
   * No-op when called before init(), when the item isn't part of any batch,
   * or when the batch is already fetched/in-flight/retries-exhausted.
   */
  prefetchBatchForItem(itemIdx: number): void {
    if (!this.abortController) return
    const batchIdx = this.registry.batchFor(itemIdx)
    if (batchIdx === undefined) return
    void this.executeFetch(batchIdx)
  }

  /**
   * Resolves once all in-flight fetches have settled. Useful for tests and
   * consumers that need deterministic sequencing.
   */
  async whenIdle(): Promise<void> {
    while (this.registry.inflightCount() > 0) {
      await Promise.allSettled(this.registry.inflightPromises())
    }
  }

  private async flushPending(): Promise<void> {
    const availableSlots =
      LazyBatchLoader.MAX_CONCURRENT_FETCHES - this.registry.inflightCount()
    if (availableSlots <= 0) return

    const candidates = new Set<number>()
    for (const itemIdx of this.monitor.visibleItems) {
      const batchIdx = this.registry.batchFor(itemIdx)
      if (batchIdx !== undefined && !this.registry.isHandled(batchIdx)) {
        candidates.add(batchIdx)
      }
    }
    if (candidates.size === 0) return

    const ranked = rankByViewportDistance(
      [...candidates],
      this.registry.allBatches,
      this.monitor.visibleItems
    )
    const promises = ranked
      .slice(0, availableSlots)
      .map((batchIdx) => this.executeFetch(batchIdx))

    await Promise.allSettled(promises)
  }

  private executeFetch(batchIdx: number): Promise<void> {
    const existing = this.registry.getInflight(batchIdx)
    if (existing) return existing
    if (this.registry.isHandled(batchIdx)) return Promise.resolve()
    if (!this.abortController) return Promise.resolve()

    const signal = this.abortController.signal
    const promise = this.fetchBatch(batchIdx, signal)
      .then(() => {
        if (signal.aborted) return
        this.registry.markFetched(batchIdx)
      })
      .catch(() => {
        if (signal.aborted) return
        this.registry.markFailed(batchIdx)
      })
      .finally(() => {
        if (signal.aborted) return
        this.registry.clearInflight(batchIdx)
        // Capacity just opened up — drain any visible-but-unfetched batches
        // that flushPending bailed on earlier when slots were full. Without
        // this, IntersectionObserver's silence after scroll-stop leaves
        // pending batches stuck forever.
        void this.flushPending()
      })

    this.registry.markInflight(batchIdx, promise)
    return promise
  }
}
